import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { LegalLayout, LegalSection } from './LegalLayout';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 15,
  border: '1px solid #E5E0DB',
  borderRadius: 8,
  marginTop: 6,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 14,
  fontWeight: 600,
  color: '#2D2D2D',
  marginTop: 16,
};

export const DataDeletion: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !description.trim()) {
      setError('Please complete all fields.');
      return;
    }
    setStatus('submitting');
    setError(null);
    try {
      await addDoc(collection(db, 'dataDeletionRequests'), {
        name: name.trim(),
        email: email.trim(),
        description: description.trim(),
        status: 'pending',
        source: 'web-form',
        createdAt: serverTimestamp(),
      });
      setStatus('success');
    } catch (err: any) {
      console.error('Data deletion request failed:', err);
      setStatus('error');
      setError('Something went wrong submitting your request. Please email support@veraaba.com.');
    }
  };

  return (
    <LegalLayout title="Data Deletion Request">
      <p>
        You can request deletion of personal information that VERA Health
        Technologies, LLC holds about you. Because VERA stores clinical records on
        behalf of ABA therapy practices, some records must be retained to meet
        legal, regulatory, and treatment-record obligations; where retention is
        required, we will restrict and de-identify data instead of deleting it and
        will explain what applies to your request.
      </p>

      <LegalSection heading="How the process works">
        <ol style={{ margin: '8px 0', paddingLeft: 22, lineHeight: 1.7 }}>
          <li>Submit the form below (or email <a href="mailto:support@veraaba.com" style={{ color: '#4F6367', fontWeight: 600 }}>support@veraaba.com</a>).</li>
          <li>We verify your identity and, if the data belongs to a practice's record, coordinate with that practice.</li>
          <li>We confirm what will be deleted, restricted, or retained under law.</li>
          <li>We complete the request and send written confirmation.</li>
        </ol>
      </LegalSection>

      <LegalSection heading="Timeline">
        <p>
          We acknowledge requests within <strong>5 business days</strong> and aim
          to complete verified requests within <strong>30 days</strong>. Complex
          requests may take up to <strong>90 days</strong>, in which case we will
          notify you of the delay.
        </p>
      </LegalSection>

      <LegalSection heading="Submit a request">
        {status === 'success' ? (
          <div
            style={{
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: 8,
              padding: 16,
              color: '#166534',
              marginTop: 8,
            }}
          >
            <strong>Request received.</strong> We've logged your deletion request
            and will contact you at the email you provided within 5 business days.
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ marginTop: 8 }}>
            <label style={labelStyle}>
              Full name
              <input
                style={inputStyle}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
            </label>
            <label style={labelStyle}>
              Email address
              <input
                style={inputStyle}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <label style={labelStyle}>
              Describe the data you want deleted
              <textarea
                style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., my account and all records associated with this email"
                required
              />
            </label>

            {error && (
              <div style={{ color: '#ef4444', fontSize: 14, marginTop: 12 }}>{error}</div>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              style={{
                marginTop: 20,
                padding: '12px 24px',
                backgroundColor: '#4F6367',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 600,
                cursor: status === 'submitting' ? 'not-allowed' : 'pointer',
                opacity: status === 'submitting' ? 0.6 : 1,
              }}
            >
              {status === 'submitting' ? 'Submitting…' : 'Submit deletion request'}
            </button>
          </form>
        )}
      </LegalSection>
    </LegalLayout>
  );
};

export default DataDeletion;
